import { ErrorMessage, Field, Formik, Form } from "formik";
import React, { useEffect } from "react";
import * as Yup from "yup";
import { Col, Row } from "reactstrap";
import {
  paymentMethod,
  deletePaymentMethod,
} from "../../../slices/payment/thunk";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useSetCurrentUser } from "../../../slices/auth/hooks";
import { authMe } from "../../../slices/thunks";

const PaymentInfoNew = ({ currentUser }) => {
  const dispatch = useDispatch();
  const setCurrentUser = useSetCurrentUser();

  const [isUpdating, setIsUpdating] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState({
    account_holder: currentUser.paymentMethodMetadata?.accountHolder || "",
    account_number: currentUser.paymentMethodMetadata?.accountNumber || "",
    routing_number: currentUser.paymentMethodMetadata?.routingNumber || "",
  });

  const toggleIsUpdating = () => {
    setIsUpdating(!isUpdating);
    if (!isUpdating) {
      setInitialValues({
        account_holder: "",
        account_number: "",
        routing_number: "",
      });
    }
  };

  const validationSchema = Yup.object().shape({
    account_holder: Yup.string().required("Account Holder Name is required"),
    account_number: Yup.string()
      .min(6, "Bank Account Number must be at least 6 characters long")
      .required("Bank Account Number is required"),
    routing_number: Yup.string().required("Routing Number is required"),
  });

  const refreshUser = async () => {
    try {
      const actionResult = await dispatch(authMe());

      if (actionResult.error) {
        throw new Error(
          "Error fetching user data: " + actionResult.error.message
        );
      }

      if (actionResult.payload) {
        console.log("Updating currentUser with new data");
        setCurrentUser(actionResult.payload);
      } else {
        console.error("No user data found!");
      }
    } catch (error) {
      console.error("Failed to refresh user data: ", error);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong with refreshing your data!",
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      Swal.fire({
        title: "Please wait...",
        html: "Updating your payment method...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      const actionResult = await dispatch(
        paymentMethod({
          account_holder: values.account_holder,
          account_number: values.account_number,
          routing_number: values.routing_number,
        })
      );
      Swal.close();

      const { payload, error } = actionResult;

      if (error) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text:
            payload.message ||
            "An error occurred while updating your payment method.",
        });
        setIsUpdating(true);
      } else {
        refreshUser();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Payment method ${isUpdating ? "updated" : "added"
            }  successfully!`,
        });
        setIsUpdating(false);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePaymentMethod = async () => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You are about to delete your payment method.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: "Please wait...",
          html: "Deleting your payment method...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        const actionResult = await dispatch(deletePaymentMethod());
        Swal.close();

        const { payload, error } = actionResult;

        if (error) {
          Swal.fire({
            icon: "error",
            title: "Oops!",
            text:
              payload.message ||
              "An error occurred while deleting payment method.",
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Payment method deleted successfully!",
          });
          refreshUser();
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: error.message,
      });
    }
  };

  useEffect(() => { }, [currentUser]);

  useEffect(() => {
    if (isUpdating) {
      setInitialValues({
        account_holder: "",
        account_number: "",
        routing_number: "",
      });
    } else {
      setInitialValues({
        account_holder: currentUser.paymentMethodMetadata?.accountHolder || "",
        account_number: currentUser.paymentMethodMetadata?.accountNumber || "",
        routing_number: currentUser.paymentMethodMetadata?.routingNumber || "",
      });
    }
  }, [isUpdating, currentUser]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, isSubmitting, resetForm, submitForm }) => (
          <Form>
            <Row>
              <Col md={4} className="mb-3">
                <label htmlFor="account_holder" className="form-label">
                  Account Holder Name
                </label>
                <Field
                  type="text"
                  name="account_holder"
                  className="form-control text-muted"
                  disabled={!isUpdating}
                />
                <ErrorMessage
                  component="div"
                  name="account_holder"
                  className="text-danger"
                />
              </Col>

              <Col md={4} className="mb-3">
                <label htmlFor="account_number" className="form-label">
                  Bank Account Number
                </label>
                <Field
                  type="text"
                  name="account_number"
                  className="form-control text-muted"
                  disabled={!isUpdating}
                />
                <ErrorMessage
                  component="div"
                  name="account_number"
                  className="text-danger"
                />
              </Col>
              <Col md={4} className="mb-3">
                <label htmlFor="routing_number" className="form-label">
                  Routing Number
                </label>
                <Field
                  type="text"
                  name="routing_number"
                  className="form-control text-muted"
                  disabled={!isUpdating}
                />
                <ErrorMessage
                  component="div"
                  name="routing_number"
                  className="text-danger"
                />
              </Col>
            </Row>
            <div className="d-flex justify-content-end align-items-center mt-2">
              {currentUser.paymentMethodMetadata && !isUpdating && (
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    onClick={handleDeletePaymentMethod}
                    className="btn btn-soft-primary border-primary"
                  >
                    Delete Payment Method
                  </button>
                </div>
              )}
              <button
                type="button"
                className="btn btn-primary ms-3"
                onClick={() => {
                  if (isUpdating) {
                    if (dirty) {
                      submitForm();
                    } else {
                      toggleIsUpdating();
                    }
                  } else {
                    toggleIsUpdating();
                  }
                }}
              >
                {currentUser.paymentMethodMetadata
                  ? isUpdating
                    ? !dirty
                      ? "Close"
                      : "Save"
                    : "Add Payment Method"
                  : isUpdating
                    ? !dirty
                      ? "Close"
                      : "Save"
                    : "Add Payment Method"}
              </button>

              {isUpdating && dirty && (
                <button
                  type="button"
                  className="btn btn-soft-danger"
                  onClick={() => {
                    resetForm();
                    toggleIsUpdating();
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PaymentInfoNew;
